import { makeAutoObservable } from "mobx";
import { RootStore } from "@intrasync/stores";

const TAB_NAMES = ["general", "structures", "lineitems", "schedule"];

export default class JobDetailViewStore {
  projectId: string | null = null;
  isAddingLineItem = false;

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as any);
  }

  setProjectId(id: string | null) {
    this.projectId = id;
  }

  get activeTab(): number {
    const tabName =
      (this.rootStore as any).routerStore?.queryParams?.tab || "general";
    const index = TAB_NAMES.indexOf(tabName);
    return index >= 0 ? index : 0;
  }

  setActiveTab(index: number) {
    const tabName = TAB_NAMES[index] || "general";
    (this.rootStore as any).routerStore.goTo(null, null, {
      ...(this.rootStore as any).routerStore.queryParams,
      tab: tabName,
    });
  }

  // --- Entity accessors ---

  get allProjects() {
    return (
      ((this.rootStore as any).manholeProjectsStore as any)?.entities || []
    );
  }

  get project() {
    if (!this.projectId) return null;
    const projects =
      ((this.rootStore as any).manholeProjectsStore as any)?.entities || [];
    return (
      projects.find(
        (p: any) => p.id === this.projectId || p.projectId === this.projectId
      ) || null
    );
  }

  get structures() {
    if (!this.projectId) return [];
    const allStructures =
      ((this.rootStore as any).manholeStructuresStore as any)?.entities || [];
    return allStructures
      .filter((s: any) => s.projectId === this.projectId)
      .sort((a: any, b: any) => {
        if (
          a.sequenceOrder !== undefined &&
          b.sequenceOrder !== undefined
        ) {
          return a.sequenceOrder - b.sequenceOrder;
        }
        return (
          new Date(a.createdAt || 0).getTime() -
          new Date(b.createdAt || 0).getTime()
        );
      });
  }

  get quote() {
    if (!this.projectId) return null;
    const allQuotes =
      ((this.rootStore as any).manholeQuotesStore as any)?.entities || [];
    return (
      allQuotes.find(
        (q: any) =>
          q.manholeProjectId === this.projectId ||
          q.projectId === this.projectId
      ) || null
    );
  }

  get quoteItems() {
    const quoteId = this.quote?.id;
    if (!quoteId) return [];
    const allItems =
      ((this.rootStore as any).manholeQuoteItemsStore as any)?.entities || [];
    return allItems
      .filter((item: any) => item.quoteId === quoteId)
      .sort((a: any, b: any) => (a.lineNumber || 0) - (b.lineNumber || 0));
  }

  get structureCodes() {
    return (
      ((this.rootStore as any).structureCodesStore as any)?.entities || []
    );
  }

  get customers() {
    return (
      ((this.rootStore as any).customersStore as any)?.entities || []
    );
  }

  get components() {
    return (
      ((this.rootStore as any).manholeComponentsStore as any)?.entities || []
    );
  }

  // --- Per-structure data ---

  getStructureComponents(structureId: string) {
    const allSequences =
      ((this.rootStore as any).manholeSequencesStore as any)?.entities || [];
    return allSequences
      .filter((s: any) => s.structureId === structureId)
      .sort(
        (a: any, b: any) => (a.sequenceOrder || 0) - (b.sequenceOrder || 0)
      );
  }

  getStructurePipes(structureId: string) {
    const allPipes =
      ((this.rootStore as any).manholePipeConnectionsStore as any)?.entities ||
      [];
    return allPipes
      .filter((p: any) => p.structureId === structureId)
      .sort((a: any, b: any) => (a.pipeNumber || 0) - (b.pipeNumber || 0));
  }

  getStructureQuoteItems(structureId: string) {
    return this.quoteItems.filter(
      (item: any) => item.structureId === structureId
    );
  }

  getStructureCodeName(structureCodeId: string) {
    if (!structureCodeId) return null;
    const code = this.structureCodes.find(
      (c: any) => c.id === structureCodeId
    );
    return code?.name || code?.code || null;
  }

  getStructurePrice(structureId: string) {
    const items = this.getStructureQuoteItems(structureId);
    return items.reduce(
      (sum: number, item: any) =>
        sum + (item.extendedCost || (item.unitCost || 0) * (item.quantity || 1)),
      0
    );
  }

  getStructurePieceCount(structureId: string) {
    const components = this.getStructureComponents(structureId);
    return components.reduce(
      (sum: number, c: any) => sum + (c.quantity || 1),
      0
    );
  }

  getStructureWeight(structureId: string) {
    const components = this.getStructureComponents(structureId);
    return components.reduce(
      (sum: number, c: any) => sum + (c.weight || 0),
      0
    );
  }

  getEffectiveStructureStatus(structure: any): string {
    // If the structure has an explicit status beyond "draft", respect it
    if (structure.status && structure.status !== "draft") {
      return structure.status;
    }

    // If the project has a more advanced status (quoted, awarded, completed),
    // structures inherit it — the backend doesn't update structure-level status
    // when a quote is generated, so we derive it here
    const projectStatus = this.project?.status;
    if (projectStatus && projectStatus !== "draft") {
      return projectStatus;
    }

    return structure.status || "draft";
  }

  getRimToInvert(structure: any) {
    if (
      structure.rimElevation != null &&
      structure.lowestInvert != null
    ) {
      return structure.rimElevation - structure.lowestInvert;
    }
    return null;
  }

  // --- Job-level totals ---

  get jobTotals() {
    const quote = this.quote;
    if (quote) {
      return {
        weight: quote.totalWeight || 0,
        pieces: quote.totalComponents || 0,
        subtotal: quote.subtotal || 0,
        tax: quote.tax || 0,
        total: quote.total || 0,
      };
    }

    // Fallback: compute from structures if no quote exists
    let weight = 0;
    let pieces = 0;
    let subtotal = 0;

    this.structures.forEach((s: any) => {
      weight += this.getStructureWeight(s.id);
      pieces += this.getStructurePieceCount(s.id);
      subtotal += this.getStructurePrice(s.id);
    });

    return {
      weight,
      pieces,
      subtotal,
      tax: 0,
      total: subtotal,
    };
  }

  // --- Line item CRUD ---

  async addQuoteItem(data: {
    structureId?: string;
    itemType: string;
    componentCode?: string;
    componentName?: string;
    description?: string;
    quantity?: number;
    unitCost?: number;
    heightInches?: number;
    diameterInches?: number;
    weight?: number;
  }) {
    const quote = this.quote;
    if (!quote) return null;

    const nextLineNumber =
      this.quoteItems.length > 0
        ? Math.max(...this.quoteItems.map((i: any) => i.lineNumber || 0)) + 1
        : 1;

    const quoteItemsStore = (this.rootStore as any).manholeQuoteItemsStore;
    if (!quoteItemsStore) return null;

    const quantity = data.quantity || 1;
    const unitCost = data.unitCost || 0;

    return quoteItemsStore.create({
      quoteId: quote.id,
      lineNumber: nextLineNumber,
      extendedCost: unitCost * quantity,
      ...data,
    });
  }

  async updateQuoteItem(
    id: string,
    data: {
      description?: string;
      quantity?: number;
      unitCost?: number;
    }
  ) {
    const quoteItemsStore = (this.rootStore as any).manholeQuoteItemsStore;
    if (!quoteItemsStore) return null;

    const patch: any = { ...data };
    if (data.quantity != null || data.unitCost != null) {
      const existing = this.quoteItems.find((i: any) => i.id === id);
      const qty = data.quantity ?? existing?.quantity ?? 1;
      const cost = data.unitCost ?? existing?.unitCost ?? 0;
      patch.extendedCost = cost * qty;
    }

    return quoteItemsStore.patch(id, patch);
  }

  async removeQuoteItem(id: string) {
    const quoteItemsStore = (this.rootStore as any).manholeQuoteItemsStore;
    if (!quoteItemsStore) return null;
    return quoteItemsStore.remove(id);
  }

  startAddingLineItem() {
    this.isAddingLineItem = true;
  }

  stopAddingLineItem() {
    this.isAddingLineItem = false;
  }

  // --- Inline stack editing ---

  async addComponentToStructure(
    structureId: string,
    component: any
  ) {
    const sequencesStore = (this.rootStore as any).manholeSequencesStore;
    if (!sequencesStore) return null;

    const existing = this.getStructureComponents(structureId);
    const nextOrder =
      existing.length > 0
        ? Math.max(...existing.map((c: any) => c.sequenceOrder || 0)) + 1
        : 1;

    return sequencesStore.create({
      structureId,
      manholeProjectId: this.projectId,
      sequenceOrder: nextOrder,
      componentType: component.componentType || "OTHER",
      componentCode: component.componentCode,
      componentName:
        component.componentName || component.description || "Unknown",
      heightInches: component.heightInches || 0,
      diameterInches: component.diameterInches,
      weight: component.weight,
      cost: component.estimatedCost,
      quantity: 1,
    });
  }

  async removeComponentFromStructure(sequenceId: string) {
    const sequencesStore = (this.rootStore as any).manholeSequencesStore;
    if (!sequencesStore) return null;
    return sequencesStore.remove(sequenceId);
  }

  reset() {
    this.projectId = null;
    this.isAddingLineItem = false;
  }
}
